/**
 * Mock Data Provider
 * 
 * Generates a realistic analysis dataset from the seed/resource data
 * (course types, site approvals, instructor qualifications, scheduling rules)
 * so the draft schedule generator can run without Dataverse.
 * 
 * Enable via: REACT_APP_USE_MOCK_DATA=true
 */

// ── Seed Data (from dev-docs/seed-data CSVs) ──

const COURSE_TYPES = [
  { id: 'ct-bst', name: 'Basic Safety Training', code: 'BST', defaultDurationDays: 5 },
  { id: 'ct-advff', name: 'Advanced Firefighting', code: 'ADVFF', defaultDurationDays: 5 },
  { id: 'ct-medcare', name: 'Medical Care Provider', code: 'MEDCARE', defaultDurationDays: 3 },
  { id: 'ct-surv', name: 'Survival Craft (Lifeboat)', code: 'SURV', defaultDurationDays: 5 },
  { id: 'ct-frb', name: 'Fast Rescue Boat', code: 'FRB', defaultDurationDays: 3 },
  { id: 'ct-tankfam', name: 'Tank Ship Familiarization', code: 'TANKFAM', defaultDurationDays: 5 },
  { id: 'ct-radar', name: 'Radar Observer', code: 'RADAR', defaultDurationDays: 5 },
  { id: 'ct-gmdss', name: 'GMDSS Radio Operator', code: 'GMDSS', defaultDurationDays: 5 },
];

const LOCATIONS = [
  { id: 'loc-sd', name: 'San Diego, CA', region: 'WestCoast' },
  { id: 'loc-nor', name: 'Norfolk, VA', region: 'EastCoast' },
  { id: 'loc-ph', name: 'Pearl Harbor, HI', region: 'WestCoast' },
  { id: 'loc-gro', name: 'Groton, CT', region: 'EastCoast' },
  { id: 'loc-bre', name: 'Bremerton, WA', region: 'WestCoast' },
  { id: 'loc-kb', name: 'Kings Bay, GA', region: 'EastCoast' },
  { id: 'loc-may', name: 'Mayport, FL', region: 'EastCoast' },
  { id: 'loc-yok', name: 'Yokosuka, JP', region: 'OCONUS' },
  { id: 'loc-sas', name: 'Sasebo, JP', region: 'OCONUS' },
  { id: 'loc-gua', name: 'Guam', region: 'OCONUS' },
  { id: 'loc-rot', name: 'Rota, Spain', region: 'OCONUS' },
  { id: 'loc-nap', name: 'Naples, Italy', region: 'OCONUS' },
  { id: 'loc-bah', name: 'Bahrain', region: 'OCONUS' },
];

// Site approvals: [locationId, courseTypeId, maxStudents]
const SITE_APPROVALS_RAW = [
  ['loc-sd','ct-bst',40],['loc-sd','ct-advff',30],['loc-sd','ct-medcare',24],['loc-sd','ct-surv',24],['loc-sd','ct-frb',12],['loc-sd','ct-tankfam',20],['loc-sd','ct-radar',20],['loc-sd','ct-gmdss',16],
  ['loc-nor','ct-bst',40],['loc-nor','ct-advff',30],['loc-nor','ct-medcare',24],['loc-nor','ct-surv',24],['loc-nor','ct-frb',12],['loc-nor','ct-tankfam',20],['loc-nor','ct-radar',20],['loc-nor','ct-gmdss',16],
  ['loc-ph','ct-bst',20],['loc-ph','ct-advff',16],['loc-ph','ct-medcare',12],['loc-ph','ct-surv',12],['loc-ph','ct-frb',8],['loc-ph','ct-tankfam',12],['loc-ph','ct-radar',12],['loc-ph','ct-gmdss',10],
  ['loc-gro','ct-bst',20],['loc-gro','ct-advff',16],['loc-gro','ct-medcare',12],['loc-gro','ct-surv',12],['loc-gro','ct-tankfam',12],
  ['loc-bre','ct-bst',24],['loc-bre','ct-advff',20],['loc-bre','ct-medcare',16],['loc-bre','ct-surv',16],['loc-bre','ct-frb',8],['loc-bre','ct-tankfam',12],['loc-bre','ct-radar',12],['loc-bre','ct-gmdss',10],
  ['loc-kb','ct-bst',16],['loc-kb','ct-advff',12],['loc-kb','ct-medcare',12],['loc-kb','ct-surv',12],
  ['loc-may','ct-bst',20],['loc-may','ct-advff',16],['loc-may','ct-medcare',12],['loc-may','ct-surv',12],['loc-may','ct-frb',8],['loc-may','ct-tankfam',12],['loc-may','ct-radar',12],['loc-may','ct-gmdss',10],
  ['loc-yok','ct-bst',16],['loc-yok','ct-advff',12],['loc-yok','ct-medcare',10],['loc-yok','ct-surv',10],['loc-yok','ct-frb',6],['loc-yok','ct-tankfam',10],['loc-yok','ct-radar',10],['loc-yok','ct-gmdss',8],
  ['loc-sas','ct-bst',12],['loc-sas','ct-advff',12],['loc-sas','ct-medcare',10],['loc-sas','ct-surv',8],
  ['loc-gua','ct-bst',12],['loc-gua','ct-advff',12],['loc-gua','ct-medcare',10],['loc-gua','ct-surv',8],['loc-gua','ct-frb',6],['loc-gua','ct-radar',8],['loc-gua','ct-gmdss',8],
  ['loc-rot','ct-bst',16],['loc-rot','ct-advff',12],['loc-rot','ct-medcare',10],['loc-rot','ct-surv',10],['loc-rot','ct-frb',6],['loc-rot','ct-tankfam',10],['loc-rot','ct-radar',10],['loc-rot','ct-gmdss',8],
  ['loc-nap','ct-bst',12],['loc-nap','ct-advff',12],['loc-nap','ct-medcare',10],['loc-nap','ct-surv',8],['loc-nap','ct-radar',8],['loc-nap','ct-gmdss',8],
  ['loc-bah','ct-bst',12],['loc-bah','ct-advff',12],['loc-bah','ct-medcare',10],['loc-bah','ct-surv',8],['loc-bah','ct-frb',6],['loc-bah','ct-tankfam',10],['loc-bah','ct-radar',8],['loc-bah','ct-gmdss',8],
];

const INSTRUCTORS = [
  { id: 'inst-001', name: 'David Adams' },
  { id: 'inst-002', name: 'Christopher Anderson' },
  { id: 'inst-003', name: 'John Baker' },
  { id: 'inst-004', name: 'Michael Brown' },
  { id: 'inst-005', name: 'Robert Clark' },
  { id: 'inst-006', name: 'James Davis' },
  { id: 'inst-007', name: 'William Evans' },
  { id: 'inst-008', name: 'Richard Fisher' },
  { id: 'inst-009', name: 'Thomas Garcia' },
  { id: 'inst-010', name: 'Daniel Harris' },
  { id: 'inst-011', name: 'Mark Johnson' },
];

// Instructor qualifications: [instructorId, courseTypeId]
const INSTRUCTOR_QUALS_RAW = [
  ['inst-001','ct-advff'],['inst-001','ct-bst'],
  ['inst-002','ct-medcare'],
  ['inst-003','ct-tankfam'],['inst-003','ct-radar'],
  ['inst-004','ct-gmdss'],
  ['inst-005','ct-bst'],
  ['inst-006','ct-advff'],['inst-006','ct-surv'],
  ['inst-007','ct-frb'],['inst-007','ct-surv'],
  ['inst-008','ct-bst'],['inst-008','ct-medcare'],
  ['inst-009','ct-radar'],['inst-009','ct-gmdss'],
  ['inst-010','ct-tankfam'],['inst-010','ct-advff'],
  ['inst-011','ct-bst'],['inst-011','ct-frb'],
];

const SCHEDULING_RULES = [
  { id: 'rule-001', name: 'No simultaneous West Coast courses', ruleType: 1, courseType1Id: null, courseType2Id: null, location1Id: null, location2Id: null, regionScope: 1, minGapDays: null, isEnabled: true, description: 'Ensure courses on west coast are not held simultaneously' },
  { id: 'rule-002', name: 'No simultaneous E/W Engineering', ruleType: 1, courseType1Id: null, courseType2Id: null, location1Id: null, location2Id: null, regionScope: null, minGapDays: null, isEnabled: true, description: 'East/west Engineering courses should not be held simultaneously' },
  { id: 'rule-003', name: 'GMDSS SD-Norfolk 1-week gap', ruleType: 2, courseType1Id: 'ct-gmdss', courseType2Id: 'ct-gmdss', location1Id: 'loc-sd', location2Id: 'loc-nor', regionScope: null, minGapDays: 7, isEnabled: true, description: 'GMDSS for SD and Norfolk at least one week apart' },
  { id: 'rule-004', name: 'ECDIS/Radar Alameda-HI gap', ruleType: 2, courseType1Id: 'ct-radar', courseType2Id: 'ct-radar', location1Id: 'loc-ph', location2Id: null, regionScope: 1, minGapDays: 7, isEnabled: true, description: 'ECDIS & Radar for Alameda and HI separated by one week' },
  { id: 'rule-005', name: 'Auto-populate instructors by qual', ruleType: 4, courseType1Id: null, courseType2Id: null, location1Id: null, location2Id: null, regionScope: null, minGapDays: null, isEnabled: true, description: 'Auto-populate instructors based on selected course proficiency' },
  { id: 'rule-006', name: 'Auto-tentative historical courses', ruleType: 3, courseType1Id: null, courseType2Id: null, location1Id: null, location2Id: null, regionScope: null, minGapDays: null, isEnabled: true, description: 'Create tentative courses for classes historically convened but cancelled previous year' },
  { id: 'rule-007', name: 'Extra sessions for waitlisted courses', ruleType: 3, courseType1Id: null, courseType2Id: null, location1Id: null, location2Id: null, regionScope: null, minGapDays: null, isEnabled: true, description: 'Create additional sessions for high-demand courses with waitlists' },
  { id: 'rule-008', name: 'Instructor N/A auto-removal', ruleType: 4, courseType1Id: null, courseType2Id: null, location1Id: null, location2Id: null, regionScope: null, minGapDays: null, isEnabled: true, description: 'When instructor marked N/A, remove from assigned sessions and flag TBD' },
];

// ── Synthetic Session Generator ──

const seededRandom = (seed) => {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
};

const HISTORY_FLOOR_YEAR = 2023;

const generateSyntheticSessions = (yearsBack = 3) => {
  const rand = seededRandom(42); // Deterministic for reproducibility
  const sessions = [];
  const now = new Date();
  const currentYear = now.getFullYear();

  // Build site approval lookup for capacity
  const approvalMap = {};
  SITE_APPROVALS_RAW.forEach(([locId, ctId, max]) => {
    approvalMap[`${locId}-${ctId}`] = max;
  });

  // For each year in the range, generate sessions (never before 2023)
  const startYear = Math.max(HISTORY_FLOOR_YEAR, currentYear - yearsBack);
  for (let year = startYear; year <= currentYear; year++) {
    COURSE_TYPES.forEach(course => {
      // Determine how many sessions per year for this course (varies by course popularity)
      const baseFrequency = course.code === 'BST' ? 12
        : course.code === 'ADVFF' ? 8
        : course.code === 'MEDCARE' ? 6
        : course.code === 'SURV' ? 6
        : course.code === 'GMDSS' ? 4
        : course.code === 'RADAR' ? 4
        : course.code === 'TANKFAM' ? 3
        : 2; // FRB

      // Add some year-to-year variation
      const frequency = Math.max(1, baseFrequency + Math.floor((rand() - 0.5) * 3));

      // Distribute across the year — pick months with some spread
      const usedMonths = new Set();
      for (let i = 0; i < frequency; i++) {
        let month;
        let attempts = 0;
        do {
          month = Math.floor(rand() * 12);
          attempts++;
        } while (usedMonths.has(month) && attempts < 20);
        usedMonths.add(month);

        // Pick a location that is approved for this course
        const approvedLocs = SITE_APPROVALS_RAW
          .filter(([, ctId]) => ctId === course.id)
          .map(([locId]) => locId);

        if (approvedLocs.length === 0) return;
        const locId = approvedLocs[Math.floor(rand() * approvedLocs.length)];
        const location = LOCATIONS.find(l => l.id === locId);
        const maxCap = approvalMap[`${locId}-${course.id}`] || 20;

        // Pick a Monday in the month
        const firstDay = new Date(year, month, 1);
        const dayOfWeek = firstDay.getDay();
        const daysToMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;
        const weekOffset = Math.floor(rand() * 3) * 7; // 1st, 2nd, or 3rd Monday
        const startDate = new Date(year, month, 1 + daysToMonday + weekOffset);

        if (startDate.getMonth() !== month) {
          startDate.setDate(startDate.getDate() - 7);
        }

        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + course.defaultDurationDays - 1);

        // Simulate enrollment (60-100% fill rate)
        const fillRate = 0.6 + rand() * 0.4;
        const enrolled = Math.round(maxCap * fillRate);

        // Pick a qualified instructor
        const qualifiedInsts = INSTRUCTOR_QUALS_RAW
          .filter(([, ctId]) => ctId === course.id)
          .map(([instId]) => instId);
        const instructorId = qualifiedInsts.length > 0
          ? qualifiedInsts[Math.floor(rand() * qualifiedInsts.length)]
          : null;
        const instructor = instructorId ? INSTRUCTORS.find(i => i.id === instructorId) : null;

        const sessionId = `mock-${course.code}-${year}-${month}-${i}`;

        sessions.push({
          id: sessionId,
          name: course.name,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          seatsTaken: enrolled,
          maxCapacity: maxCap,
          actualEnrolled: enrolled,
          fillRate,
          locationGuid: locId,
          locationName: location?.name || '',
          department: null,
          departmentName: '',
          classroom: null,
          classroomName: '',
          campus: null,
          campusName: location?.region || '',
          courseTypeId: course.id,
          courseTypeName: course.name,
          courseStatus: null,
          courseStatusName: '',
          cancelled: false,
          stateCode: 0,
          statusCode: 1,
          instructorId,
          instructorName: instructor?.name || '',
        });
      }
    });
  }

  sessions.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  console.log(`[MockData] Generated ${sessions.length} synthetic sessions across ${yearsBack + 1} years`);
  return sessions;
};

// ── Public API (mirrors scheduleGeneratorService.buildAnalysisDataset) ──

const buildMockAnalysisDataset = (yearsBack = 3, onProgress = null) => {
  const report = (phase, message) => {
    console.log(`[MockData] ${phase}: ${message}`);
    if (onProgress) onProgress(phase, message);
  };

  report('sessions', 'Generating synthetic historical sessions...');
  const sessions = generateSyntheticSessions(yearsBack);
  report('sessions', `Generated ${sessions.length} sessions`);

  report('locations', 'Loading mock locations...');
  report('instructors', 'Loading mock instructors...');

  const siteApprovals = SITE_APPROVALS_RAW.map(([locId, ctId, max], i) => ({
    id: `sa-${i}`,
    locationId: locId,
    locationName: LOCATIONS.find(l => l.id === locId)?.name || '',
    courseTypeId: ctId,
    courseTypeName: COURSE_TYPES.find(c => c.id === ctId)?.name || '',
    maxStudents: max,
    approvalStatus: 1,
    approvalStatusName: 'Current',
  }));

  const qualifications = INSTRUCTOR_QUALS_RAW.map(([instId, ctId], i) => ({
    id: `qual-${i}`,
    instructorId: instId,
    instructorName: INSTRUCTORS.find(ins => ins.id === instId)?.name || '',
    courseTypeId: ctId,
    courseTypeName: COURSE_TYPES.find(c => c.id === ctId)?.name || '',
    isActive: true,
    expirationDate: null,
  }));

  report('qualifications', `Loaded ${qualifications.length} quals, ${siteApprovals.length} site approvals, ${SCHEDULING_RULES.length} rules`);
  report('complete', 'Mock analysis dataset ready');

  return {
    success: true,
    data: {
      sessions,
      locations: LOCATIONS,
      instructors: INSTRUCTORS,
      courseTypes: COURSE_TYPES,
      qualifications,
      siteApprovals,
      schedulingRules: SCHEDULING_RULES,
      dateRange: {
        start: new Date(Math.max(new Date(`${HISTORY_FLOOR_YEAR}-01-01T00:00:00Z`), new Date(new Date().setFullYear(new Date().getFullYear() - yearsBack)))),
        end: new Date()
      }
    }
  };
};

export { COURSE_TYPES, LOCATIONS, INSTRUCTORS, SCHEDULING_RULES, SITE_APPROVALS_RAW, INSTRUCTOR_QUALS_RAW };
export default buildMockAnalysisDataset;
